import React, { useEffect } from "react";
import { Clock, CheckCircle2 } from "lucide-react";
import { trackButton, trackPageEvent } from "@/lib/analytics";
import { Reveal, PageTransition } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const trustItems = [
  { icon: Clock, label: "We respond within 1 business day" },
  { icon: CheckCircle2, label: "Bring your real documents for a live extraction" },
];

const Contact = () => {
  useEffect(() => {
    // Calendly inline widget script (customer-provided embed code)
    if (!document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
    // Calendly booking funnel events (official postMessage lifecycle)
    const onCalendly = (e) => {
      if (e.origin !== "https://calendly.com" || !e.data?.event) return;
      if (e.data.event === "calendly.event_type_viewed") trackPageEvent("calendly_widget_viewed");
      else if (e.data.event === "calendly.date_and_time_viewed") trackPageEvent("calendly_date_time_viewed");
      else if (e.data.event === "calendly.date_and_time_selected") trackButton("calendly_time_selected");
      else if (e.data.event === "calendly.event_scheduled") trackButton("calendly_booking_completed");
    };
    window.addEventListener("message", onCalendly);
    return () => window.removeEventListener("message", onCalendly);
  }, []);

  return (
    <PageTransition>
      <Seo
        title="Contact Sales | Book a Demo | Hewto.ai"
        description="Talk to the Hewto.ai team about automating your HCFA, UB-04, and dental claim processing. Book a personalized demo and see live extraction on your own documents."
        keywords="hewto.ai contact sales, book a demo healthcare document ai, claims automation demo"
        canonical="https://www.hewto.ai/contact-sales"
      />
      <section className="relative overflow-hidden border-b border-slate-100 bg-slate-50">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:px-8 sm:py-20">
          <Reveal>
            <span className="inline-block rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-600">
              Contact Sales
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Let&apos;s process your documents together
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Book a tailored demo and bring your real HCFA, UB-04, or dental samples — watch
              Hewto.ai extract them live. Pick a time below and we&apos;ll tailor the session to
              your workflow.
            </p>
            <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {trustItems.map((t) => (
                <li key={t.label} className="flex items-center gap-2 text-sm text-slate-600">
                  <t.icon className="h-4 w-4 text-brand" aria-hidden="true" />
                  {t.label}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Calendly scheduling widget */}
      <section className="bg-white py-12 sm:py-16" data-testid="calendly-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Pick a time that works for you
            </h2>
            <p className="mt-3 text-center text-slate-600">
              Schedule a 30-minute call with our team — no back-and-forth emails needed.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              className="calendly-inline-widget mt-8"
              data-url="https://calendly.com/contactsales-hewto/30min?primary_color=026aa6"
              style={{ minWidth: 320, height: 700 }}
              data-testid="calendly-widget"
            />
          </Reveal>
        </div>
      </section>
    </PageTransition>
  );
};

export default Contact;
